import React from 'react';
import {Text, View} from '@react-pdf/renderer';
import {IntlShape} from 'react-intl';
import QuotePage from './QuotePage';
import SectionLabel from './SectionLabel';
import {formatQuoteDate} from '../quoteDocument';
import {buildStyles} from './quotePdfStyles';
import type {QuoteDocumentData, QuoteTemplate} from '../resolver/types';

type QuoteAgreementPageProps = {
	data: QuoteDocumentData;
	template: QuoteTemplate;
	intl: IntlShape;
};

type SignBlockProps = {
	party: string;
	styles: ReturnType<typeof buildStyles>;
	first: boolean;
	intl: IntlShape;
};

const SignBlock = ({party, styles, first, intl}: SignBlockProps): JSX.Element => (
	<View style={first ? styles.signPartyFirst : styles.signParty}>
		<Text style={styles.signOrg}>{party}</Text>
		<View>
			<View style={styles.signSignatureSpace} />
			<View style={styles.signLine} />
			<Text style={styles.signLabel}>{intl.formatMessage({id: 'quote_designer.pdf.signature'})}</Text>
		</View>
		<View style={styles.signField}>
			<View style={styles.signLine} />
			<Text style={styles.signLabel}>{intl.formatMessage({id: 'quote_designer.pdf.full_name'})}</Text>
		</View>
		<View style={styles.signField}>
			<View style={styles.signLine} />
			<Text style={styles.signLabel}>{intl.formatMessage({id: 'quote_designer.pdf.signer_title'})}</Text>
		</View>
		<View style={styles.signField}>
			<View style={styles.signLine} />
			<Text style={styles.signLabel}>{intl.formatMessage({id: 'common.date'})}</Text>
		</View>
	</View>
);

/** The always-included signature page: agreement sentence, two signature blocks, digital note. */
const QuoteAgreementPage = ({data, template, intl}: QuoteAgreementPageProps): JSX.Element => {
	const styles = buildStyles(template.font);
	return (
		<QuotePage data={data} template={template} intl={intl}>
			<View style={styles.section}>
				<SectionLabel font={template.font} brandColor={template.brandColor}>
					{intl.formatMessage({id: 'quote_designer.section.agreement'})}
				</SectionLabel>
				<Text style={styles.paragraph}>
					{intl.formatMessage(
						{id: 'quote_designer.pdf.agreement_body'},
						{reference: data.projectIndicator, expiry: formatQuoteDate(data.expiryDate, intl)}
					)}
				</Text>
				<View style={styles.signGrid}>
					<SignBlock party={data.companyName} styles={styles} first intl={intl} />
					<SignBlock party={data.clientName || data.projectName} styles={styles} first={false} intl={intl} />
				</View>
				<Text style={styles.signNote}>{intl.formatMessage({id: 'quote_designer.pdf.digital_signature_note'})}</Text>
			</View>
		</QuotePage>
	);
};

export default QuoteAgreementPage;
